import { findGame, type BotDifficulty } from '@cardengine/shared';
import { useState } from 'react';
import { Chat } from '../components/Chat.js';
import { FreecellRulesEditor } from '../components/FreecellRulesEditor.js';
import { MinesweeperRulesEditor } from '../components/MinesweeperRulesEditor.js';
import { NoughtsRulesEditor } from '../components/NoughtsRulesEditor.js';
import { RulesEditor } from '../components/RulesEditor.js';
import { SolitaireRulesEditor } from '../components/SolitaireRulesEditor.js';
import { SpiderRulesEditor } from '../components/SpiderRulesEditor.js';
import { CARD_BACKS } from '../cardBacks.js';
import { useStore } from '../store.js';

const DIFFICULTIES: { value: BotDifficulty; label: string }[] = [
  { value: 'easy', label: 'Easy' },
  { value: 'medium', label: 'Medium' },
  { value: 'hard', label: 'Hard' },
];

function ShareRow({ code, isHost }: { code: string; isHost: boolean }) {
  const [copied, setCopied] = useState<'code' | 'link' | null>(null);
  const [confirmingNewCode, setConfirmingNewCode] = useState(false);
  const regenerateCode = useStore((s) => s.regenerateCode);

  async function copy(kind: 'code' | 'link') {
    const value = kind === 'code' ? code : `${window.location.origin}/?join=${code}`;
    try {
      await navigator.clipboard.writeText(value);
      setCopied(kind);
      setTimeout(() => setCopied(null), 1800);
    } catch {
      // Clipboard blocked (insecure origin or denied) — the code is on screen anyway.
    }
  }

  // Two taps: rolling the code invalidates every invite already sent out, so it
  // should not be one stray click away.
  function handleNewCode() {
    if (!confirmingNewCode) {
      setConfirmingNewCode(true);
      setTimeout(() => setConfirmingNewCode(false), 5_000);
      return;
    }
    setConfirmingNewCode(false);
    regenerateCode();
  }

  return (
    <div className="share">
      <div className="share__code">
        <span className="share__label">Table code</span>
        <strong className="share__value">{code}</strong>
      </div>
      <div className="share__buttons">
        <button type="button" className="btn btn--ghost btn--small" onClick={() => copy('code')}>
          {copied === 'code' ? 'Copied' : 'Copy code'}
        </button>
        <button type="button" className="btn btn--ghost btn--small" onClick={() => copy('link')}>
          {copied === 'link' ? 'Copied' : 'Copy invite link'}
        </button>
        {isHost && (
          <button
            type="button"
            className={`btn btn--small ${confirmingNewCode ? 'btn--danger' : 'btn--ghost'}`}
            onClick={handleNewCode}
            title="Issue a new code so anyone holding the old one can't get back in"
          >
            {confirmingNewCode ? 'Confirm — old code dies' : 'New code'}
          </button>
        )}
      </div>
    </div>
  );
}

/** A personal preference, so it sits apart from the house rules. */
function CardBackPicker() {
  const cardBack = useStore((s) => s.cardBack);
  const setCardBack = useStore((s) => s.setCardBack);

  return (
    <section className="backs" aria-label="Card backs">
      <div className="backs__head">
        <h2 className="seats__title">Your card backs</h2>
        <span className="backs__note">Only you see this</span>
      </div>
      <div className="backs__row">
        {CARD_BACKS.map((back) => (
          <button
            key={back.id}
            type="button"
            className={`backs__swatch ${cardBack === back.id ? 'is-active' : ''}`}
            onClick={() => setCardBack(back.id)}
            aria-pressed={cardBack === back.id}
            title={back.label}
          >
            <span
              className="backs__card"
              style={{ background: `linear-gradient(150deg, ${back.from} 0%, ${back.to} 100%)` }}
            >
              <span className="backs__pattern" />
            </span>
            <span className="backs__label">{back.label}</span>
          </button>
        ))}
      </div>
      <SoundToggle />
    </section>
  );
}

/**
 * Lives with the card backs because it is the same kind of setting: yours
 * alone, and nothing to do with how the table plays.
 */
function SoundToggle() {
  const sound = useStore((s) => s.sound);
  const setSound = useStore((s) => s.setSound);

  return (
    <label className="backs__sound">
      <input type="checkbox" checked={sound} onChange={(event) => setSound(event.target.checked)} />
      <span>
        Sound
        <span className="backs__hint">A chime when it is your turn, even in another tab</span>
      </span>
    </label>
  );
}

export function LobbyRoom() {
  const lobby = useStore((s) => s.lobby);
  const session = useStore((s) => s.session);
  const addBot = useStore((s) => s.addBot);
  const removeMember = useStore((s) => s.removeMember);
  const startGame = useStore((s) => s.startGame);
  const leaveLobby = useStore((s) => s.leaveLobby);
  const [difficulty, setDifficulty] = useState<BotDifficulty>('medium');

  if (!lobby || !session) return null;

  const isHost = lobby.hostId === session.playerId;
  const canStart = lobby.members.length >= lobby.minPlayers;
  const isFull = lobby.members.length >= lobby.maxPlayers;
  // Asked of the catalogue rather than of the game id, so the next game on a
  // shelf that is not the card one gets the right wording and the right
  // controls without editing this file. It was `kind === 'paper'` until the
  // arcade shelf arrived, which is exactly the mistake this comment was written
  // to prevent: the question is whether there is a deck, and naming it after
  // the one kind that was not cards answered it correctly by accident.
  const usesCards = findGame(lobby.gameType)?.kind === 'cards';

  return (
    <main className="lobby">
      <div className="lobby__main">
        <header className="lobby__header">
          <div>
            <h1 className="lobby__title">Your table</h1>
            <p className="lobby__subtitle">
              {lobby.members.length} of {lobby.maxPlayers} seats
              {lobby.roundsPlayed > 0 && ` · ${lobby.roundsPlayed} round${lobby.roundsPlayed === 1 ? '' : 's'} played`}
            </p>
          </div>
          <button type="button" className="btn btn--ghost" onClick={() => void leaveLobby()}>
            Leave
          </button>
        </header>

        <ShareRow code={lobby.code} isHost={isHost} />

        <section className="seats" aria-label="Players">
          <h2 className="seats__title">Players</h2>
          <ul className="seats__list">
            {lobby.members.map((member) => (
              <li key={member.id} className={`seats__item ${!member.connected ? 'is-away' : ''}`}>
                <span className="seats__name">
                  {member.name}
                  {member.id === session.playerId && <span className="seats__tag">you</span>}
                  {member.isHost && <span className="seats__tag seats__tag--host">host</span>}
                  {member.isBot && (
                    <span className="seats__tag seats__tag--bot">{member.botDifficulty} AI</span>
                  )}
                  {!member.connected && !member.isBot && (
                    <span className="seats__tag seats__tag--away">away</span>
                  )}
                </span>
                {lobby.roundsPlayed > 0 && (
                  // Labelled, unlike the scoreboard's bare figure: there is no
                  // column header here to say what the number counts.
                  <span className="seats__wins">
                    {member.wins} {member.wins === 1 ? 'win' : 'wins'}
                  </span>
                )}
                {isHost && member.id !== session.playerId && (
                  <button
                    type="button"
                    className="btn btn--ghost btn--small"
                    onClick={() => removeMember(member.id)}
                    aria-label={`Remove ${member.name}`}
                  >
                    Remove
                  </button>
                )}
              </li>
            ))}
          </ul>

          {/* A one-seat game has nobody to add. The server has always refused
              this — "Solitaire is played on your own" — but offering a control
              whose only outcome is that message is a small lie about the game. */}
          {isHost && lobby.maxPlayers > 1 && (
            <div className="seats__add">
              <label className="field field--inline">
                <span className="field__label">Add an AI</span>
                <select
                  className="field__input"
                  value={difficulty}
                  onChange={(event) => setDifficulty(event.target.value as BotDifficulty)}
                >
                  {DIFFICULTIES.map((d) => (
                    <option key={d.value} value={d.value}>
                      {d.label}
                    </option>
                  ))}
                </select>
              </label>
              <button
                type="button"
                className="btn btn--secondary"
                onClick={() => addBot(difficulty)}
                disabled={isFull}
              >
                Add player
              </button>
            </div>
          )}
        </section>

        {/* Nothing on a table without a deck has a back to pick. */}
        {usesCards && <CardBackPicker />}

        {lobby.gameType === 'solitaire' ? (
          <SolitaireRulesEditor rules={lobby.rules} editable={isHost} />
        ) : lobby.gameType === 'freecell' ? (
          <FreecellRulesEditor rules={lobby.rules} editable={isHost} />
        ) : lobby.gameType === 'spider' ? (
          <SpiderRulesEditor rules={lobby.rules} editable={isHost} />
        ) : lobby.gameType === 'noughts-and-crosses' ? (
          <NoughtsRulesEditor rules={lobby.rules} editable={isHost} />
        ) : lobby.gameType === 'minesweeper' ? (
          <MinesweeperRulesEditor rules={lobby.rules} editable={isHost} />
        ) : (
          <RulesEditor rules={lobby.rules} editable={isHost} />
        )}

        <div className="lobby__start">
          {isHost ? (
            <button
              type="button"
              className="btn btn--primary btn--large"
              onClick={startGame}
              disabled={!canStart}
            >
              {canStart
                ? usesCards
                  ? 'Deal the cards'
                  : 'Start the game'
                : `Need at least ${lobby.minPlayers} players`}
            </button>
          ) : (
            <p className="lobby__waiting">Waiting for the host to start the game…</p>
          )}
        </div>
      </div>

      <aside className="lobby__side">
        <Chat />
      </aside>
    </main>
  );
}
